import * as yup from 'yup';

export interface PriceBookEntryParams {
  offset: number;
  take: number;
  sourceProductNumber?: string;
  productNumber?: string;
  condition?: string;
  effectiveDate?: string;
  effectiveDateFrom?: string;
  effectiveDateTo?: string;
  includeDeletedData?: boolean;
}

export interface PriceBookEntryPayload {
  productNumber: string;
  condition: string;
  type: string;
  listPrice: number;
  unitPrice: number;
  discountPercentage: number;
  effectiveDate: string;
  endDate?: string | null;
}

export enum PriceBookEntryLabels {
  productNumber = 'Product Number',
  condition = 'Condition',
  type = 'Type',
  listPrice = 'List Price',
  unitPrice = 'Unit Price',
  discountPercentage = 'Discount %',
  effectiveDate = 'Effective Date',
  endDate = 'End Date'
}

export const priceBookEntryPayloadSchema: yup.SchemaOf<PriceBookEntryPayload> = yup.object({
  productNumber: yup.string().label(PriceBookEntryLabels.productNumber).max(100).required(),
  condition: yup.string().label(PriceBookEntryLabels.condition).max(100).required(),
  type: yup.string().label(PriceBookEntryLabels.type).max(100).required(),
  listPrice: yup.number().label(PriceBookEntryLabels.listPrice).min(0).required(),
  unitPrice: yup.number().label(PriceBookEntryLabels.unitPrice).min(0).required(),
  discountPercentage: yup.number().label(PriceBookEntryLabels.discountPercentage).min(0).max(100).required(),
  effectiveDate: yup.string().label(PriceBookEntryLabels.effectiveDate).required(),
  endDate: yup
    .string()
    .label(PriceBookEntryLabels.endDate)
    .nullable()
    .test('end-after-start', 'End Date cannot be before the Effective Date', function (value) {
      const { effectiveDate } = this.parent;

      if (!value || !effectiveDate) return true;

      return new Date(value) >= new Date(effectiveDate);
    })
});

export interface PriceBookEntry {
  id: string;
  productNumber: string;
  condition: string;
  type: string;
  listPrice: number;
  unitPrice: number;
  discountPercentage: number;
  effectiveDate: string;
  endDate: string | null;
  isDeleted: boolean;
  dataAreaId: string;
  businessId: string;
  documentType: string;
  business: { dataAreaId: string; erpId: string; id: string; name: string };
  createdByFullName: string;
  createdByUserId: string;
  createdDateTime: string;
  modifiedByFullName: string;
  modifiedByUserId: string;
  modifiedDateTime: string;
}

export interface PriceBookEntryImport {
  productNumber: string;
  condition: string;
  type: string;
  listPrice: number;
  unitPrice: number;
  discountPercentage: number;
  effectiveDate: string;
  endDate: string;
}

export const priceBookEntryImportSchema = yup.object({
  productNumber: yup.string().label(PriceBookEntryLabels.productNumber).max(100).required(),
  condition: yup.string().label(PriceBookEntryLabels.condition).max(100).required(),
  type: yup.string().label(PriceBookEntryLabels.type).max(100).required(),
  listPrice: yup.number().label(PriceBookEntryLabels.listPrice).typeError('List Price must be a number').min(0).required(),
  unitPrice: yup.number().label(PriceBookEntryLabels.unitPrice).typeError('Unit Price must be a number').min(0).required(),
  discountPercentage: yup.number().label(PriceBookEntryLabels.discountPercentage).typeError('Discount % must be a number').min(0).max(100).required(),
  effectiveDate: yup.string().label(PriceBookEntryLabels.effectiveDate).required(),
  endDate: yup.string().label(PriceBookEntryLabels.endDate).nullable()
});

export interface CurrentPriceEntry {
  id?: string;
  productNumber: string;
  condition: string;
  type: string;
  listPrice: number;
  unitPrice: number;
  discountPercentage: number;
  effectiveDate: string;
  endDate: string | null;
}

export interface CurrentPriceResponse {
  prices: CurrentPriceEntry[];
}
